import React from 'react';
import { LLreward } from './loyaltyTypes';
import { openSignInModal } from '../../../../../templates/_shared/ui-components/SignInModal/utils';
import { claimReward } from './loyaltyUtils';
import { CartData } from '../../../../../utils/cart';
import { loyaltyState, openLoyaltyVariantSelectorModal } from './loyaltyState';
import { Customer } from '../../../../../utils/environment';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from '@material-ui/core';

let RewardCard = ({
  reward,
  settings,
  cartData,
  isRedeeming,
  setIsRedeeming,
}: {
  reward: LLreward;
  settings: {
    redeemButtonText: string;
    shouldHideRedeemButton: boolean;
    redeemAgainButtonText: string;
    alreadyRedeemedButtonText: string;
    redeemingButtonText: string;
  };
  cartData: CartData;
  isRedeeming: boolean;
  setIsRedeeming: (value: boolean) => void;
}) => {
  const { title, description, point_cost, session_options } = reward;
  const {
    redeemButtonText,
    redeemAgainButtonText,
    shouldHideRedeemButton,
    alreadyRedeemedButtonText,
    redeemingButtonText,
  } = settings;
  const pointCost = `${point_cost} point${point_cost > 1 && 's'}`;
  const isAlreadyRedeemed = cartData.giftItemsAndLoyaltyRewards.find(
    (item) => item.properties._loyalty_reward_id === reward.id
  );
  const isCustomer = !!Customer.id;
  const isRandomVariantReward = reward.random_variant_reward;
  const isDesktop = useMediaQuery('(min-width: 992px)', { noSsr: true });

  const isSessionLimited = session_options?.session_limit === 1;
  const isButtonDisabled =
    (isCustomer && !!isAlreadyRedeemed && isSessionLimited) ||
    (isCustomer && !!(loyaltyState.pointsBalance && loyaltyState.pointsBalance <= point_cost)) ||
    (isCustomer && isRedeeming);

  const getButtonText = () => {
    if (isCustomer) {
      if (isSessionLimited && isAlreadyRedeemed) {
        return alreadyRedeemedButtonText;
      } else if (!isSessionLimited && isAlreadyRedeemed && !isButtonDisabled) {
        return redeemAgainButtonText;
      } else if (isRedeeming) {
        return redeemingButtonText;
      } else if (!isButtonDisabled) {
        return redeemButtonText;
      } else {
        return 'More Points Needed';
      }
    } else {
      return 'Sign In';
    }
  };

  const handleOnClick = async () => {
    const rewardMarket = reward.market?.trim().length > 0 ? reward.market : 'domestic';
    if (isCustomer && reward.target_products && reward.target_products.length === 1) {
      setIsRedeeming(true);
      await claimReward(reward.id, reward.target_products[0]!.variant_id.toString(), rewardMarket); // ? [REVISIT] Add UI feedback in case of fail
      setIsRedeeming(false);
    } else if (isCustomer && reward.target_products && reward.target_products.length > 1) {
      if (isRandomVariantReward) {
        const genericVariant = reward.target_products.find((product) => product.buyable);
        const genericVariantId = genericVariant ? genericVariant.variant_id : null;
        if (!genericVariantId) {
          console.error('No buyable variant found for the reward.');
          return;
        }
        setIsRedeeming(true);
        await claimReward(reward.id, genericVariantId.toString(), rewardMarket);
        setIsRedeeming(false);
      } else {
        openLoyaltyVariantSelectorModal(reward, redeemButtonText);
      }
    } else {
      openSignInModal({ returnUrl: window.location.href });
    }
  };

  return isRandomVariantReward ? (
    <div className="grouped-reward-box">
      <div className="reward-and-variants">
        <div className="target-products">
          {reward.target_products
            .filter((product) => !product.buyable)
            .map((product) => (
              <div className="card">
                <img src={product.variant_image_url ?? ''} alt="" className="img-fluid image" />
                <div className="info-container">
                  <div className="info">
                    <p className="title body-small semibold">{product.title}</p>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className={`reward-availability ${isDesktop ? 'desktop' : 'mobile'}`}>
        <p className="points-cost tag-small uppercase">{pointCost}</p>
        <div className="description body">
          Your order will include <span>one</span> of these reward items, based on availability.
        </div>
        <RedeemButton
          isButtonDisabled={isButtonDisabled}
          shouldHideRedeemButton={shouldHideRedeemButton}
          isCustomer={isCustomer}
          onClick={handleOnClick}
          buttonText={getButtonText()}
        />
      </div>
    </div>
  ) : (
    <>
      <div className="card">
        <img src={reward.target_products[0]?.image_url} alt="" className="img-fluid image" />
        <div className="info-container">
          <div className="info">
            <p className="title body-small semibold">{title}</p>
            <p className="points-cost tag-small uppercase">{pointCost}</p>
            <p className="description legal">{description}</p>
          </div>
          <RedeemButton
            isButtonDisabled={isButtonDisabled}
            shouldHideRedeemButton={shouldHideRedeemButton}
            isCustomer={isCustomer}
            onClick={handleOnClick}
            buttonText={getButtonText()}
          />
        </div>
      </div>
    </>
  );
};

const RedeemButton = ({
  isButtonDisabled,
  shouldHideRedeemButton,
  isCustomer,
  onClick,
  buttonText,
}: {
  isButtonDisabled: boolean;
  shouldHideRedeemButton: boolean;
  isCustomer: boolean;
  onClick: () => Promise<void>;
  buttonText: string;
}) => {
  if (shouldHideRedeemButton && isCustomer) {
    return null;
  }

  return (
    <button
      disabled={isButtonDisabled}
      style={isButtonDisabled ? { cursor: 'not-allowed' } : {}}
      className="action-button button-primary-xs"
      type="button"
      onClick={onClick}
    >
      {buttonText}
    </button>
  );
};

RewardCard = observer(RewardCard);
export { RewardCard };
